import Skeleton from "@ui-kit/feedback/Skeleton/Skeleton.web";
import TickCircle from "@ui-kit/icons/TickCircle/TickCircle.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import Button from "@ui-kit/form/Button/Button.web";
import Link from "@ui-kit/navigation/Link/Link.web";
import { useTranslation } from "@shared/utils/internationalization";
import type { GroupSalesOrderStatusProps } from "./GroupSalesOrderStatus.props";

export default function GroupSalesOrderStatus(
  props: GroupSalesOrderStatusProps
): JSX.Element {
  const { transactionId, basket, isLoading } = props;

  const { t } = useTranslation("checkout");
  const groupSalesClaimLink = basket?.group_sales_claim_link || "";

  if (isLoading) {
    return (
      <Flex direction="column" align="center" gap="xl">
        <Skeleton>
          <Box h="element.4xl" w="element.4xl" />
        </Skeleton>
        <Skeleton h="117px" w="100%" />
        <Skeleton h="element.4xl" w="100%" />
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="2xl" data-testid="group-sales-order-status">
      {/* Success Header */}
      <Flex direction="column" align="center" gap="xl">
        <TickCircle.Solid
          h="element.4xl"
          w="element.4xl"
          colorPalette="primary"
        />
        <Box align="center">
          <Text fontSize="4xl" fontWeight="600" color="primary.800" mb="xs">
            {t("Order confirmed")}
          </Text>
          {transactionId && (
            <Text>
              <Text as="span">{t("Order ID")}</Text>{" "}
              <Text
                as="span"
                fontSize="sm"
                fontWeight={700}
                data-testid="group-sales-order-status-transaction-id"
              >
                {transactionId}
              </Text>
            </Text>
          )}
        </Box>
      </Flex>

      {/* Distribution Instructions */}
      <Box borderTop="1px solid" borderColor="gray.200" pt="2xl" mt="md">
        <Text fontSize="xl" fontWeight="600" color="primary.800" mb="md">
          {t("How to distribute your tickets")}
        </Text>

        <Flex direction="column" gap="md">
          {/* Step 1 */}
          <Flex gap="sm" align="flex-start">
            <Flex
              minW="element.xl"
              h="element.xl"
              borderRadius="50%"
              bg="primary.600"
              color="bg"
              fontSize="xs"
              align="center"
              justify="center"
              fontWeight="600"
            >
              1
            </Flex>
            <Text flex={1} lineHeight="1.6">
              {t(
                "Click on the 'Access Distribution Portal' button below to accept the tickets. If you don't have a Showpass account, you'll be prompted to create one."
              )}
            </Text>
          </Flex>

          {/* Step 2 */}
          <Flex gap="sm" align="flex-start">
            <Flex
              minW="element.xl"
              h="element.xl"
              borderRadius="50%"
              bg="primary.600"
              color="bg"
              fontSize="xs"
              align="center"
              justify="center"
              fontWeight="600"
            >
              2
            </Flex>
            <Text flex={1} lineHeight="1.6">
              {t(
                "Once in the portal, select 'Customize email' to personalize the message sent to your group members."
              )}
            </Text>
          </Flex>

          {/* Step 3 */}
          <Flex gap="sm" align="flex-start">
            <Flex
              minW="element.xl"
              h="element.xl"
              borderRadius="50%"
              bg="primary.600"
              color="bg"
              fontSize="xs"
              align="center"
              justify="center"
              fontWeight="600"
            >
              3
            </Flex>
            <Text flex={1} lineHeight="1.6">
              {t(
                "Next, go back to the distribution page, select 'Distribute items' and choose the tickets you want to send to members of your group."
              )}
            </Text>
          </Flex>

          {/* Step 4 */}
          <Flex gap="sm" align="flex-start">
            <Flex
              minW="element.xl"
              h="element.xl"
              borderRadius="50%"
              bg="primary.600"
              color="bg"
              fontSize="xs"
              align="center"
              justify="center"
              fontWeight="600"
            >
              4
            </Flex>
            <Text flex={1} lineHeight="1.6">
              {t(
                "Keep track of your distributions and manage them easily by using the History Page, where you can view their status, cancel, or void previously distributed items."
              )}
            </Text>
          </Flex>
        </Flex>

        {/* Access Distribution Portal Button */}
        {groupSalesClaimLink && (
          <Flex mt="2xl" justify="center">
            <Link
              href={groupSalesClaimLink}
              width={{ base: "100%", sm: "auto" }}
            >
              <Button.Solid
                width={{ base: "100%", sm: "auto" }}
                data-testid="group-sales-access-portal-button"
              >
                {t("Access distribution portal")}
              </Button.Solid>
            </Link>
          </Flex>
        )}
      </Box>

      {/* Help Articles */}
      <Box borderTop="1px solid" borderColor="gray.200" pt="2xl" mt="md">
        <Text fontSize="md" fontWeight="600" color="primary.800" mb="md">
          {t("Help Articles")}
        </Text>
        <Flex direction="column" gap="sm">
          <Link
            href="https://help.showpass.com/hc/en-us/articles/13009187646231"
            target="_blank"
            rel="noopener noreferrer"
            color="primary.600"
            _hover={{ textDecoration: "underline" }}
          >
            {t("Claiming Your Account and Order")}
          </Link>
          <Link
            href="https://help.showpass.com/hc/en-us/articles/13009193692695"
            target="_blank"
            rel="noopener noreferrer"
            color="primary.600"
            _hover={{ textDecoration: "underline" }}
          >
            {t("Distributing and Transferring Items")}
          </Link>
          <Link
            href="https://help.showpass.com/hc/en-us/articles/13009213790103"
            target="_blank"
            rel="noopener noreferrer"
            color="primary.600"
            _hover={{ textDecoration: "underline" }}
          >
            {t("Voiding Claimed Transfers")}
          </Link>
        </Flex>
      </Box>
    </Flex>
  );
}

export type { GroupSalesOrderStatusProps };
